import React from 'react';
import { Row, Col } from 'antd';
import { observer } from 'mobx-react';
import { DriverAssignInfoDtoModelType } from '../../../../../store/mst/models';
import NumberOfRoutes from './NumberOfRoutes';
import DriverProfile from '../../../../drivers/DriverProfile';
import { Theme, Body, SegmentedProgressBar } from '@routific/hive';
import { COLUMN_WIDTHS, FamiliarityCol, NumberOfRoutesCol, SpeedCol } from './StyledCols';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { formatSpeedPercentageLabel, speedMultiplierToPercentage } from '../../../../../helpers/drivers/speed';

export const StyledBody = styled(Body)`
  margin-left: ${Theme.Spacing.xsSpacing};
`;

export const StyledSpeedBody = styled(Body)<{ $isDefault: boolean }>`
  opacity: ${({ $isDefault }) => ($isDefault ? 0.5 : 1)};
`;

const StyledSpeedCol = styled(SpeedCol)`
  min-width: 0;
  justify-content: flex-start;
`;

export const StyledRow = styled(Row)`
  width: 100%;
  padding: ${Theme.Spacing.smSpacing} ${Theme.Spacing.mdSpacing};
  &.active-driver-option {
    background-color: ${Theme.Colors.primary2};
  }
`;

interface Props {
  driver: DriverAssignInfoDtoModelType;
  routeColors: string[];
  isActive?: boolean;
}

const DriverOption: React.FC<Props> = ({ driver, routeColors, isActive = false }) => {
  const { t } = useTranslation('common');
  const lengthOfRouteColors = routeColors.length;
  const speedPercentage = speedMultiplierToPercentage(driver.speed);
  const isDefaultSpeed = speedPercentage === 0;
  const speedLabel = formatSpeedPercentageLabel(speedPercentage, t('driverFields.speed.default'));

  return (
    <StyledRow
      wrap={false}
      className={isActive ? 'active-driver-option' : ''}
      data-cy={isActive ? 'active-driver-option' : 'driver-option'}
    >
      <Col flex={COLUMN_WIDTHS.name}>
        <DriverProfile driverName={driver.name} avatarSize="small" deleted={driver.deleted ?? false} />
      </Col>
      <NumberOfRoutesCol>
        <div>
          <NumberOfRoutes routeColors={routeColors} />
          <StyledBody
            data-cy="driver-route-count"
            type={lengthOfRouteColors === 0 ? 'tertiary' : 'secondary'}
          >
            {lengthOfRouteColors === 0 ? '--' : lengthOfRouteColors}
          </StyledBody>
        </div>
      </NumberOfRoutesCol>
      <FamiliarityCol>
        {driver.familiarityScore && (
          <SegmentedProgressBar filledSegments={driver.familiarityScore.rating} totalSegments={3} stepWidth={21} />
        )}
      </FamiliarityCol>
      <StyledSpeedCol>
        <StyledSpeedBody type="secondary" $isDefault={isDefaultSpeed}>
          {speedLabel}
        </StyledSpeedBody>
      </StyledSpeedCol>
    </StyledRow>
  );
};

export default observer(DriverOption);
